import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, ArrowLeft, PartyPopper, MapPin, Clock, ExternalLink, Gift, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { APPLE_MAPS_URL, GOOGLE_MAPS_URL } from "@/lib/wedding";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

const OPTIONS = [
  { key: "attending", label: "Joyfully Accepts", icon: Check },
  { key: "declined", label: "Regretfully Declines", icon: X },
];

export default function RsvpPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [site, setSite] = useState(null);
  const [error, setError] = useState("");
  const [guests, setGuests] = useState([]);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("edit"); // 'summary' | 'edit'
  const [justSubmitted, setJustSubmitted] = useState(false);

  const load = () => {
    api.get(`/rsvp/${token}`)
      .then((r) => {
        setData(r.data);
        setGuests(r.data.guests.map((g) => ({ ...g })));
        setNote(r.data.household.guest_note || "");
        setMode(r.data.household.rsvp_submitted_at ? "summary" : "edit");
        trackEvent("rsvp_view", {
          household: r.data.household.invite_name,
          token,
          already_responded: !!r.data.household.rsvp_submitted_at,
        });
      })
      .catch((e) => setError(apiError(e.response?.data?.detail, "This invitation link is not active.")));
  };

  useEffect(() => {
    api.get("/site/content").then((r) => setSite(r.data)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const setAttendance = (id, val) => setGuests((gs) => gs.map((g) => (g.id === id ? { ...g, attendance: val } : g)));
  const setName = (id, val) => setGuests((gs) => gs.map((g) => (g.id === id ? { ...g, name: val } : g)));
  const scrollToForm = () => document.querySelector("#respond")?.scrollIntoView({ behavior: "smooth" });

  const submit = async () => {
    for (const g of guests) {
      if (g.is_plus_one && g.attendance === "attending" && !g.name.trim()) {
        toast.error("Please enter a name for your additional guest, or mark them as not attending.");
        return;
      }
      if (g.attendance === "awaiting") {
        toast.error("Please choose a response for each guest.");
        return;
      }
    }
    setSubmitting(true);
    try {
      await api.post(`/rsvp/${token}`, {
        guests: guests.map((g) => ({ id: g.id, name: g.name, attendance: g.attendance })),
        note,
      });
      trackEvent("rsvp_submitted", {
        household: data.household.invite_name,
        token,
        attending: guests.filter((g) => g.attendance === "attending").length,
        declined: guests.filter((g) => g.attendance === "declined").length,
      });
      toast.success("Response saved");
      setJustSubmitted(true);
      setMode("summary");
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <Shell>
        <div className="text-center max-w-md" data-testid="rsvp-error">
          <h1 className="font-display text-4xl text-ivory">We couldn't find that invitation</h1>
          <p className="mt-4 text-cream/75">{error}</p>
          <p className="mt-6 text-cream/75">
            Please reach out to Gracie Brewer at <a href="tel:9188220339" className="text-gold underline">918-822-0339</a> and we'll get you sorted.
          </p>
          <Link to="/rsvp" className="mt-8 inline-flex items-center gap-2 text-gold hover:underline"><ArrowLeft className="w-4 h-4" /> Find my invitation</Link>
        </div>
      </Shell>
    );
  }

  if (!data) return <Shell><p className="text-gold font-display text-2xl">Loading your invitation...</p></Shell>;

  // Already responded: show a summary with links
  if (mode === "summary") {
    return (
      <Shell>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg text-center" data-testid="rsvp-summary">
          {justSubmitted && <PartyPopper className="w-12 h-12 text-gold mx-auto mb-6" strokeWidth={1.3} />}
          <p className="text-[11px] tracking-luxe uppercase text-gold">{justSubmitted ? "Thank you!" : "Your response"}</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl text-ivory font-light">{data.household.invite_name}</h1>
          <p className="mt-4 text-cream/75 text-sm">
            {justSubmitted ? "Your response has been saved." : "Here is what you told us."} You can update it anytime before {data.deadline_display}.
          </p>

          <div className="mt-8 space-y-2 text-left">
            {guests.map((g) => (
              <div key={g.id} className="flex items-center justify-between rounded-sm border border-gold-soft bg-espresso-dark/60 px-5 py-3" data-testid={`summary-guest-${g.id}`}>
                <span className="text-ivory font-display text-xl">{g.name || "Guest"}</span>
                {g.attendance === "attending" ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-gold"><Check className="w-4 h-4" /> Attending</span>
                ) : g.attendance === "declined" ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-cream/60"><X className="w-4 h-4" /> Not attending</span>
                ) : (
                  <span className="text-sm text-cream/50">No response</span>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-3">
            <button onClick={() => { setJustSubmitted(false); setMode("edit"); setTimeout(scrollToForm, 50); }} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gold text-espresso-dark tracking-[0.12em] uppercase text-sm hover:bg-ivory transition-colors" data-testid="summary-edit-btn">
              <Pencil className="w-4 h-4" /> Edit my response
            </button>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="/#registry" className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-gold text-gold tracking-[0.12em] uppercase text-sm hover:bg-gold hover:text-espresso-dark transition-colors" data-testid="summary-registry-btn">
                <Gift className="w-4 h-4" /> Registry
              </a>
              <Link to="/" className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-gold text-gold tracking-[0.12em] uppercase text-sm hover:bg-gold hover:text-espresso-dark transition-colors" data-testid="summary-website-btn">
                <ExternalLink className="w-4 h-4" /> Full website
              </Link>
            </div>
          </div>
        </motion.div>
      </Shell>
    );
  }

  // Edit / respond view
  return (
    <div className="min-h-screen w-full bg-espresso text-ivory font-sans">
      <section className="px-6 py-16 sm:py-24 max-w-2xl mx-auto text-center">
        <Link to="/" className="inline-flex items-center gap-2 text-gold/80 hover:text-gold text-sm mb-10"><ArrowLeft className="w-4 h-4" /> Angeo &amp; Gracie</Link>
        <p className="text-[11px] tracking-luxe uppercase text-gold">You are invited</p>
        <h1 className="mt-4 font-display text-4xl sm:text-6xl text-ivory font-light">{data.household.invite_name}</h1>
        {site && (
          <div className="mt-10 grid sm:grid-cols-2 gap-4 text-left">
            <div className="rounded-sm border border-gold-soft bg-espresso-dark/60 p-6">
              <Clock className="w-5 h-5 text-gold mb-3" strokeWidth={1.3} />
              <p className="text-ivory">{site.date_display}</p>
              <p className="text-cream/70 text-sm">Guests seated for ceremony by {site.ceremony_time}</p>
              <p className="text-cream/70 text-sm">{site.setting_description}</p>
            </div>
            <div className="rounded-sm border border-gold-soft bg-espresso-dark/60 p-6">
              <MapPin className="w-5 h-5 text-gold mb-3" strokeWidth={1.3} />
              <p className="text-ivory">{site.location_name}</p>
              <p className="text-cream/70 text-sm">{site.address}</p>
              <div className="mt-1 flex gap-3 text-sm">
                <a href={APPLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline" data-testid="rsvp-apple-maps">Apple Maps</a>
                <span className="text-cream/30">·</span>
                <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline" data-testid="rsvp-google-maps">Google Maps</a>
              </div>
            </div>
          </div>
        )}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={scrollToForm} className="px-8 py-3.5 rounded-full bg-gold text-espresso-dark tracking-[0.12em] uppercase text-sm hover:bg-ivory transition-colors" data-testid="go-to-respond">Respond Now</button>
          <Link to="/" className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-gold text-gold tracking-[0.12em] uppercase text-sm hover:bg-gold hover:text-espresso-dark transition-colors" data-testid="explore-site">
            <ExternalLink className="w-4 h-4" /> View Full Details
          </Link>
        </div>
      </section>

      <section id="respond" className="px-6 pb-24 max-w-2xl mx-auto" data-testid="rsvp-form">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-px bg-gold/40 mb-6" />
          <h2 className="font-display text-3xl sm:text-4xl text-ivory font-light">Kindly Respond</h2>
          <p className="mt-3 text-cream/70 text-sm">Please respond for each guest by {data.deadline_display}.</p>
        </div>

        <div className="space-y-5">
          {guests.map((g) => (
            <div key={g.id} className="rounded-sm border border-gold-soft bg-espresso-dark/60 p-5" data-testid={`guest-row-${g.id}`}>
              {g.is_plus_one ? (
                <div className="mb-4">
                  <label className="text-[11px] tracking-luxe uppercase text-gold">Additional guest</label>
                  <Input value={g.name} onChange={(e) => setName(g.id, e.target.value)} placeholder="Full name of your guest" className="mt-2 bg-espresso border-gold-soft text-ivory placeholder:text-cream/40" data-testid={`plusone-name-${g.id}`} />
                </div>
              ) : (
                <p className="font-display text-2xl text-ivory mb-4">{g.name || "Guest"}</p>
              )}
              <div className="grid grid-cols-2 gap-3">
                {OPTIONS.map((o) => {
                  const active = g.attendance === o.key;
                  return (
                    <button key={o.key} onClick={() => setAttendance(g.id, o.key)}
                      className={`flex items-center justify-center gap-2 py-3 rounded-sm border text-sm transition-colors ${
                        active ? (o.key === "attending" ? "bg-gold text-espresso-dark border-gold" : "bg-espresso border-ivory/40 text-ivory") : "border-gold-soft text-cream/70 hover:border-gold"
                      }`} data-testid={`attend-${o.key}-${g.id}`}>
                      <o.icon className="w-4 h-4" /> {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <label className="text-[11px] tracking-luxe uppercase text-gold">A note for the couple (optional)</label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Leave a little note if you'd like" className="mt-2 bg-espresso-dark/60 border-gold-soft text-ivory placeholder:text-cream/40" data-testid="rsvp-note" />
        </div>

        <p className="mt-6 text-cream/55 text-sm italic text-center">{data.children_policy}</p>

        <button onClick={submit} disabled={submitting} className="mt-8 w-full py-4 rounded-full bg-gold text-espresso-dark tracking-[0.14em] uppercase text-sm hover:bg-ivory transition-colors disabled:opacity-60" data-testid="rsvp-submit">
          {submitting ? "Saving..." : "Submit Response"}
        </button>
      </section>
    </div>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen w-full bg-espresso text-ivory font-sans flex items-center justify-center px-6 py-16">{children}</div>;
}
